import React from 'react';
import { Typography, Card, CardContent, Box, Input, Button, Stack, FormControl, FormLabel, Alert, Table, Chip, Modal, ModalDialog, CircularProgress, Divider } from '@mui/joy';
import MainLayout from '../../components/Layout/MainLayout';
import api from '../../utils/api';

const DevicesPage = () => {
  // Pairing form state
  const [code, setCode] = React.useState('');
  const [deviceName, setDeviceName] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [pairError, setPairError] = React.useState('');
  const [pairSuccess, setPairSuccess] = React.useState(null);
  const [openPair, setOpenPair] = React.useState(false);

  // Edit/Pair device state
  const [editingDevice, setEditingDevice] = React.useState(null);
  const [editCode, setEditCode] = React.useState('');
  const [editDeviceName, setEditDeviceName] = React.useState('');
  const [editLocation, setEditLocation] = React.useState('');
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editError, setEditError] = React.useState('');
  const [editSuccess, setEditSuccess] = React.useState(null);
  const [openEdit, setOpenEdit] = React.useState(false);

  // Devices list state
  const [loading, setLoading] = React.useState(false);
  const [listError, setListError] = React.useState('');
  const [devices, setDevices] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [pages, setPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const fetchDevices = async (opts = {}) => {
    try {
      setLoading(true);
      setListError('');
      const { data } = await api.get('/api/devices', {
        params: { page: opts.page || page, limit },
      });
      if (data?.success) {
        setDevices(data.devices || []);
        setPages(data.pagination?.pages || 1);
        setTotal(data.pagination?.total || 0);
      } else {
        setListError(data?.message || 'Failed to load devices');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load devices';
      setListError(msg);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDevices({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setPairError('');
    setPairSuccess(null);
    const normalizedCode = (code || '').trim();
    if (!/^\d{4}$/.test(normalizedCode)) {
      setPairError('Enter a valid 4-digit code.');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post('/api/devices/pairing/claim', {
        code: normalizedCode,
        device_name: deviceName?.trim() || undefined,
        location: location?.trim() || undefined,
      });
      if (data?.success) {
        setPairSuccess(data.device);
        setCode('');
        setDeviceName('');
        setLocation('');
        // refresh list and close after short delay
        fetchDevices({ page: 1 });
        setPage(1);
        setTimeout(() => setOpenPair(false), 800);
      } else {
        setPairError(data?.message || 'Failed to pair device');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to pair device';
      setPairError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditSuccess(null);
    const normalizedCode = (editCode || '').trim();
    if (!/^\d{4}$/.test(normalizedCode)) {
      setEditError('Enter a valid 4-digit code.');
      return;
    }
    try {
      setEditSubmitting(true);
      const { data } = await api.post(`/api/devices/${editingDevice.id}/pair`, {
        code: normalizedCode,
        device_name: editDeviceName?.trim() || undefined,
        location: editLocation?.trim() || undefined,
      });
      if (data?.success) {
        setEditSuccess(data.device);
        setEditCode('');
        setEditDeviceName('');
        setEditLocation('');
        setEditingDevice(null);
        // refresh list and close after short delay
        fetchDevices({ page: 1 });
        setPage(1);
        setTimeout(() => setOpenEdit(false), 800);
      } else {
        setEditError(data?.message || 'Failed to pair device');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to pair device';
      setEditError(msg);
    } finally {
      setEditSubmitting(false);
    }
  };

  const openEditModal = (device) => {
    setEditingDevice(device);
    setEditDeviceName(device.device_name);
    setEditLocation(device.location || '');
    setEditCode('');
    setEditError('');
    setEditSuccess(null);
    setOpenEdit(true);
  };

  return (
    <MainLayout currentPage="devices">
      <Box>
        <Typography level="h2" sx={{ fontWeight: 600, mb: 3 }}>
          Device Management
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button onClick={() => setOpenPair(true)}>Add Device</Button>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/device-groups'}
            sx={{
              '&:hover': { borderColor: '#1976d2', backgroundColor: '#f3f7ff' }
            }}
          >
            📱 Manage Groups
          </Button>
          <Button variant="outlined" onClick={() => fetchDevices()}>Refresh</Button>
          <Box sx={{ ml: 'auto', color: 'neutral.600' }}>
            {total} total devices
          </Box>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            {listError ? (
              <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                {listError}
              </Alert>
            ) : null}
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : (
              <>
                <Table hoverRow sx={{ '& thead th': { bgcolor: 'neutral.softBg' } }}>
                  <thead>
                    <tr>
                      <th style={{ width: 240 }}>Name</th>
                      <th>Device ID</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Last Seen</th>
                      <th style={{ width: 180 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices?.length ? (
                      devices.map((d) => (
                        <tr key={d.id}>
                          <td>
                            <Stack>
                              <Typography level="title-sm">{d.device_name}</Typography>
                              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>#{d.id}</Typography>
                            </Stack>
                          </td>
                          <td><Typography level="body-sm">{d.device_id}</Typography></td>
                          <td>
                            <Chip size="sm" color={d.status === 'online' ? 'success' : d.status === 'maintenance' ? 'warning' : 'neutral'} variant="soft">
                              {d.status || 'unknown'}
                            </Chip>
                          </td>
                          <td><Typography level="body-sm">{d.location || '-'}</Typography></td>
                          <td><Typography level="body-sm">{d.last_seen ? new Date(d.last_seen).toLocaleString() : '-'}</Typography></td>
                          <td>
                            <Stack direction="row" spacing={1}>
                              <Button size="sm" variant="outlined" onClick={() => openEditModal(d)}>Edit/Pair</Button>
                              <Button size="sm" color="danger" variant="outlined" onClick={async () => {
                                if (!window.confirm('Delete this device?')) return;
                                try {
                                  await api.delete(`/api/devices/${d.id}`);
                                  fetchDevices();
                                } catch (err) {
                                  alert(err?.response?.data?.message || err?.message || 'Delete failed');
                                }
                              }}>Delete</Button>
                            </Stack>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography level="body-md">No devices found.</Typography>
                            <Divider sx={{ my: 2 }} />
                            <Button onClick={() => setOpenPair(true)}>Add your first device</Button>
                          </Box>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <Button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next</Button>
                  <Box sx={{ ml: 'auto', color: 'neutral.600' }}>Page {page} of {pages}</Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        <Modal open={openPair} onClose={() => setOpenPair(false)}>
          <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
            <Typography level="title-lg" sx={{ mb: 1 }}>Add a device using 4-digit code</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
              Enter the code shown on the TV app to pair and register the device.
            </Typography>
            <form onSubmit={onSubmit}>
              <Stack spacing={2}>
                {pairError ? (
                  <Alert color="danger" variant="soft">
                    {pairError}
                  </Alert>
                ) : null}
                {pairSuccess ? (
                  <Alert color="success" variant="soft">
                    Device paired: {pairSuccess.device_name} (ID: {pairSuccess.device_id})
                  </Alert>
                ) : null}
                <FormControl required>
                  <FormLabel>4-digit code</FormLabel>
                  <Input
                    value={code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
                      setCode(v);
                    }}
                    inputMode="numeric"
                    placeholder="1234"
                    size="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Device name (optional)</FormLabel>
                  <Input
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="Lobby TV"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Location (optional)</FormLabel>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Hyderabad - Lobby"
                  />
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button type="submit" loading={submitting} disabled={submitting || code.length !== 4}>
                    Pair device
                  </Button>
                  <Button variant="outlined" onClick={() => setOpenPair(false)} disabled={submitting}>Cancel</Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        {/* Edit/Pair Device Modal */}
        <Modal open={openEdit} onClose={() => setOpenEdit(false)}>
          <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
            <Typography level="title-lg" sx={{ mb: 1 }}>Edit/Pair Device</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
              Update device information and pair with a new 4-digit code from the TV app.
            </Typography>
            <form onSubmit={onEditSubmit}>
              <Stack spacing={2}>
                {editError ? (
                  <Alert color="danger" variant="soft">
                    {editError}
                  </Alert>
                ) : null}
                {editSuccess ? (
                  <Alert color="success" variant="soft">
                    Device updated: {editSuccess.device_name} (ID: {editSuccess.device_id})
                  </Alert>
                ) : null}
                <FormControl required>
                  <FormLabel>4-digit pairing code</FormLabel>
                  <Input
                    value={editCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
                      setEditCode(v);
                    }}
                    inputMode="numeric"
                    placeholder="1234"
                    size="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Device name</FormLabel>
                  <Input
                    value={editDeviceName}
                    onChange={(e) => setEditDeviceName(e.target.value)}
                    placeholder="Lobby TV"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Input
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="Hyderabad - Lobby"
                  />
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button type="submit" loading={editSubmitting} disabled={editSubmitting || editCode.length !== 4}>
                    Update & Pair
                  </Button>
                  <Button variant="outlined" onClick={() => setOpenEdit(false)} disabled={editSubmitting}>Cancel</Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>
      </Box>
    </MainLayout>
  );
};

export default DevicesPage;